/**
 * The one way this app talks to the backend.
 *
 * Auth lives entirely in httpOnly cookies (`app/auth/routes.py`), so there is no
 * token for JS to hold, attach, or leak. That has one consequence the whole file
 * is built around: the browser silently stops sending a valid session the moment
 * the 15-minute access cookie expires, and the first symptom is a 401 on a
 * request the user did not know they were making. Rather than bounce them to the
 * login screen mid-scroll, a 401 triggers exactly one refresh and one retry.
 */
const BASE = '/'

/** The refresh cookie is scoped to `path=/auth/refresh`; this must match it exactly. */
const REFRESH_PATH = 'auth/refresh'

/**
 * Endpoints where a 401 means "the credentials you just sent are wrong", not
 * "your session expired".
 *
 * Refreshing after a failed login would fire a pointless request and replace
 * the real message ("invalid credentials") with a session error; refreshing
 * after a failed refresh would recurse. Every OTHER path gets the silent
 * refresh -- `/auth/me` above all, since checking the session on a page load is
 * exactly the request that discovers an expired 15-minute access cookie.
 */
const NO_REFRESH_PATHS = new Set([
  'auth/login',
  'auth/2fa/verify',
  // The enrollment door. Its credential is the scoped `enrollment` token, not a
  // cookie, and the pages that use it (the invite screen, the forced enrolment
  // step) have never held a session -- so a refresh here can only 401 and fire
  // `onSessionExpired()` for a session that never existed, swallowing the one
  // message that helps: "this enrolment link has expired".
  'auth/2fa/enroll',
  'auth/2fa/enroll/confirm',
  'auth/invite/accept',
  REFRESH_PATH,
])

export class ApiError extends Error {
  readonly status: number
  /** FastAPI's `{"detail": ...}` when the body carried one, else the raw text. */
  readonly detail: string

  constructor(status: number, detail: string) {
    super(`${status} ${detail}`)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

export type QueryValue = string | number | boolean | undefined | null
export type Query = Record<string, QueryValue>

/** Called when a refresh fails, i.e. the session is genuinely over. Task 20 wires
 *  this to the router so any screen can be evicted to /login from one place. */
let onSessionExpired: (() => void) | null = null
export function setSessionExpiredHandler(fn: (() => void) | null): void {
  onSessionExpired = fn
}

/**
 * In flight refresh, shared by every caller.
 *
 * The dashboard fires several queries at once (overview + families + the 1,062
 * row endpoint list). When the access cookie expires they all 401 within a few
 * milliseconds of each other. Without this latch each one would POST its own
 * refresh, and because refresh ROTATES the token — with reuse of an already
 * rotated token treated as theft and answered by revoking the whole family
 * (`/auth/refresh` in `app/auth/routes.py`) — the concurrent refreshes would log
 * the user out. One promise, shared; cleared afterwards so a later expiry can
 * refresh again.
 */
let refreshing: Promise<void> | null = null

async function refresh(): Promise<void> {
  refreshing ??= fetch(BASE + REFRESH_PATH, { method: 'POST', credentials: 'include' })
    .then(async (r) => {
      if (!r.ok) throw new ApiError(r.status, await readDetail(r))
    })
    .finally(() => {
      // Cleared on failure too: a dead refresh must not poison every later
      // request with a permanently rejected promise.
      refreshing = null
    })
  return refreshing
}

async function readDetail(res: Response): Promise<string> {
  const text = await res.text().catch(() => '')
  try {
    const parsed: unknown = JSON.parse(text)
    if (parsed && typeof parsed === 'object' && 'detail' in parsed) {
      const detail = (parsed as { detail: unknown }).detail
      return typeof detail === 'string' ? detail : JSON.stringify(detail)
    }
  } catch {
    /* not JSON — fall through to the raw body */
  }
  return text
}

function withQuery(path: string, query?: Query): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    // undefined/null mean "filter not set" and must not become `family=undefined`,
    // which the backend would happily match against zero endpoints.
    if (value === undefined || value === null) continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const relative = path.replace(/^\//, '')
  const url = BASE + relative
  const res = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  })

  if (res.status === 401 && retry && !NO_REFRESH_PATHS.has(relative.split('?')[0])) {
    try {
      await refresh() // single-flight; concurrent 401s share it
    } catch (err) {
      onSessionExpired?.()
      throw err
    }
    return request<T>(path, init, false)
  }

  if (!res.ok) throw new ApiError(res.status, await readDetail(res))

  // 204 (logout) has no body; `res.json()` would throw on the empty string.
  if (res.status === 204) return null as T
  return (await res.json()) as T
}

export const api = {
  get: <T,>(p: string, query?: Query) => request<T>(withQuery(p, query)),
  post: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  // A query, not a body: a request body on DELETE is ignored by enough
  // intermediaries that relying on one is a bug waiting for a proxy. The one
  // DELETE in this app needs it -- the target is named by id AND by the email
  // the admin typed to confirm, which the server re-checks against the row it
  // is about to destroy.
  del: <T,>(p: string, query?: Query) => request<T>(withQuery(p, query), { method: 'DELETE' }),
}
